import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Sidebar from "../../components/Sidebar";
import Navbar from "../../components/Navbar";
import axiosInstance from "../../../axiosInstance";
import { getJobPortalAuthConfig } from "../../../helpers/jobPortalAuth";
import { downloadSubscriptionInvoice } from "../../../helpers/subscriptionInvoice";

const AdminSubscriptionOrdersPage = () => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState({});
  const [audienceFilter, setAudienceFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const authConfig = await getJobPortalAuthConfig();
      const params = new URLSearchParams();
      if (audienceFilter) params.set("audience", audienceFilter);
      if (statusFilter) params.set("status", statusFilter);

      const response = await axiosInstance.get(
        `/job-portal/admin/subscription-orders?${params.toString()}`,
        authConfig
      );

      setOrders(response.data?.data?.orders || []);
      setStats(response.data?.data?.stats || {});
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Unable to load subscription orders"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [audienceFilter, statusFilter]);

  const statCards = useMemo(
    () => [
      ["Total Orders", stats.total || 0],
      ["Recruiter Orders", stats.recruiter || 0],
      ["Job Seeker Orders", stats.jobseeker || 0],
      ["Paid Orders", stats.paid || 0],
      ["Pending Orders", stats.pending || 0],
      ["Failed Orders", stats.failed || 0],
    ],
    [stats]
  );

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />
        <div className="py-4">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <div>
              <h1 className="h4 mb-1">Subscription Orders</h1>
              <p className="text-muted mb-0">
                Track recruiter and job seeker subscription purchases.
              </p>
            </div>
          </div>

          <div className="row g-3 mb-4">
            {statCards.map(([label, value]) => (
              <div className="col-md-6 col-xl-2" key={label}>
                <div className="card border-0 shadow-sm h-100">
                  <div className="card-body">
                    <div className="small text-muted">{label}</div>
                    <div className="fs-4 fw-bold">{loading ? "--" : value}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <div className="row g-3 mb-3">
                <div className="col-md-3">
                  <label className="form-label">Audience</label>
                  <select
                    className="form-select"
                    value={audienceFilter}
                    onChange={(e) => setAudienceFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="recruiter">Recruiter</option>
                    <option value="jobseeker">Job Seeker</option>
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All</option>
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>User</th>
                      <th>Audience</th>
                      <th>Plan</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Purchase Date</th>
                      <th>Invoice</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          Loading...
                        </td>
                      </tr>
                    ) : orders.length ? (
                      orders.map((order) => (
                        <tr key={order.id}>
                          <td>
                            <div className="fw-semibold">{order.user?.name || "-"}</div>
                            <div className="small text-muted">
                              {order.user?.email || "-"}
                            </div>
                          </td>
                          <td>
                            {order.audience === "recruiter"
                              ? "Recruiter"
                              : "Job Seeker"}
                          </td>
                          <td>
                            <div className="fw-semibold">
                              {order.plan?.title || "-"}
                            </div>
                            <div className="small text-muted">
                              {order.plan?.durationLabel || "-"}
                            </div>
                          </td>
                          <td>₹{order.amount || 0}</td>
                          <td>
                            <span
                              className={`badge ${
                                order.status === "paid"
                                  ? "bg-success"
                                  : order.status === "pending"
                                  ? "bg-warning text-dark"
                                  : "bg-danger"
                              }`}
                            >
                              {order.status}
                            </span>
                          </td>
                          <td>
                            {order.purchasedAt
                              ? new Date(order.purchasedAt).toLocaleDateString("en-IN")
                              : "-"}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-dark"
                              onClick={() =>
                                downloadSubscriptionInvoice(order, {
                                  title: "Admin Subscription Invoice",
                                })
                              }
                            >
                              Download
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="7" className="text-center py-4 text-muted">
                          No subscription orders found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
};

export default AdminSubscriptionOrdersPage;
